<template>
  <Dialog
    :visible="props.visible"
    @show="handleShowDialog()"
    modal
    :closable="false"
    :class="`w-11 sm:w-10 md:w-8 lg:w-7 xl:w-5`"
  >
    <template #header>
      <div class="text-xl">Create a report schedule</div>
    </template>
    <div class="grid align-items-start">
      <div class="col-12 md:col-6 flex flex-column justify-content-end gap-1">
        <label for="report_name">Schedule Name</label>
        <InputText
          id="report_name"
          v-model="scheduleData.report_name"
          :class="{
            'p-invalid': submitted && !scheduleData.report_name?.length,
          }"
        />
        <small
          v-if="submitted && !scheduleData.report_name?.length"
          class="p-error"
        >
          Please provide a name for the schedule
        </small>
      </div>
      <div class="col-12 md:col-6 flex flex-column justify-content-end gap-1">
        <label for="choose_schedule">Choose Schedule</label>
        <Dropdown
          id="choose_schedule"
          v-model="scheduleData.report_schedule"
          :options="scheduleOptions"
          optionLabel="label"
          optionValue="value"
        >
        </Dropdown>
      </div>
      <div class="col-12 flex flex-column justify-content-end gap-1 p-fluid">
        <label for="recipients">Recipient Emails</label>
        <Chips
          input-id="recipients"
          v-model="scheduleData.report_recipients"
          placeholder="Enter emails"
          separator=","
          :class="{
            'p-invalid':
              submitted && scheduleData.report_recipients.length === 0,
          }"
        />
        <small
          v-if="submitted && scheduleData.report_recipients.length === 0"
          class="p-error"
        >
          Please enter at least one recipient
        </small>
      </div>
    </div>
    <template #footer>
      <Button label="Cancel" text @click="emits('hide')" :disabled="loading">
        Cancel
      </Button>
      <Button
        class="m-0"
        label="Save"
        @click="submitSchedule()"
        :disabled="loading"
        :loading="loading"
      >
        Save
      </Button>
    </template>
  </Dialog>
</template>

<script setup>
import { API_BASE_URL } from "@/consts";
import axios from "axios";
import Button from "primevue/button";
import Chips from "primevue/chips";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";

const props = defineProps(["visible", "userRole", "dataMode", "userToken"]);
const emits = defineEmits(["hide"]);

const toast = useToast();

const submitted = ref(false);
const loading = ref(false);

const scheduleData = ref({
  report_name: "",
  report_recipients: [],
  report_schedule: "0 10 * * *",
});

const scheduleOptions = [
  { label: "Daily", value: "0 10 * * *" },
  { label: "Weekly", value: "0 10 * * 1" },
  { label: "Monthly", value: "0 10 1 * *" },
];

const resetScheduleData = () => {
  scheduleData.value = {
    report_name: "",
    report_recipients: [],
    report_schedule: "0 10 * * *",
  };
};

const submitSchedule = async () => {
  try {
    submitted.value = true;
    if (
      scheduleData.value.report_name?.length &&
      scheduleData.value.report_recipients?.length
    ) {
      loading.value = true;
      const response = await axios.post(
        `${API_BASE_URL}/scheduled-table-reports`,
        {
          ...scheduleData.value,
          user_role: props.userRole,
          data_mode: props.dataMode.endsWith("_all")
            ? props.dataMode
            : `${props.dataMode}_all`,
        },
        {
          headers: {
            Authorization: `Bearer ${props.userToken}`,
          },
        }
      );

      if (response.data.success) {
        resetScheduleData();
        loading.value = false;
        submitted.value = false;
        emits("hide");
        toast.add({
          severity: "success",
          summary: "Success",
          detail: `Schedule created successfully`,
          life: 4000,
        });
      } else {
        loading.value = false;
        submitted.value = false;
        toast.add({
          severity: "error",
          summary:
            "Schedule could not be created, " + response.status + " error",
          life: 4000,
        });
      }
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Schedule could not be created, " + error,
      life: 4000,
    });
    console.error(error);
    loading.value = false;
  }
};

const handleShowDialog = () => {
  resetScheduleData();
  loading.value = false;
  submitted.value = false;
};
</script>
